import { Controller } from "@hotwired/stimulus"

/**
 * Consent Banner Controller
 *
 * Manages cookie consent preferences with localStorage persistence.
 * Loads Google Tag Manager through a reverse proxy to bypass geo-blocks.
 * Respects GPC (Global Privacy Control) and Do Not Track signals.
 *
 * Data attributes:
 *   data-consent-banner-region-value="ru" | "eu"
 *   data-consent-banner-gtm-id-value="GTM-XXXXXXXX"
 *   data-consent-banner-proxy-url-value="https://gtag.nl.apande.ai"
 *
 * Targets:
 *   banner, settings, analyticsToggle, marketingToggle
 */
export default class extends Controller {
  static targets = ["banner", "settings", "analyticsToggle", "marketingToggle"]
  static values = {
    region: { type: String, default: "ru" },
    gtmId: String,
    proxyUrl: String,
    measurementId: String
  }

  static STORAGE_KEY = "mm:consent"
  static CONSENT_VERSION = "1.0"

  connect() {
    this.consent = this.loadConsent()

    if (this.consent) {
      // Already consented — apply preferences silently
      this.applyConsent()
      this.hideBanner()
    } else if (this.isGpcEnabled()) {
      // GPC/DNT signal — treat as reject, don't show banner
      this.saveConsent({ analytics: false, marketing: false })
      this.hideBanner()
    } else {
      // No consent yet — show banner
      // For RU: load GTM immediately (informational consent per 152-ФЗ)
      if (this.regionValue === "ru") {
        this.loadGTM()
      }
      this.showBanner()
    }
  }

  // --- Actions ---

  acceptAll() {
    this.saveConsent({ analytics: true, marketing: true })
    this.applyConsent()
    this.closeBanner()
  }

  rejectAll() {
    this.saveConsent({ analytics: false, marketing: false })
    this.applyConsent()
    this.closeBanner()
  }

  toggleSettings() {
    if (!this.hasSettingsTarget) return
    const panel = this.settingsTarget
    const isVisible = panel.dataset.visible === "true"
    panel.dataset.visible = isVisible ? "false" : "true"

    // Sync toggles with current state or defaults
    if (!isVisible) {
      const defaults = this.regionValue === "ru"
      if (this.hasAnalyticsToggleTarget) {
        this.analyticsToggleTarget.checked = this.consent
          ? this.consent.analytics
          : defaults
      }
      if (this.hasMarketingToggleTarget) {
        this.marketingToggleTarget.checked = this.consent
          ? this.consent.marketing
          : false
      }
    }
  }

  saveSettings() {
    const analytics = this.hasAnalyticsToggleTarget
      ? this.analyticsToggleTarget.checked
      : false
    const marketing = this.hasMarketingToggleTarget
      ? this.marketingToggleTarget.checked
      : false

    this.saveConsent({ analytics, marketing })
    this.applyConsent()
    this.closeBanner()
  }

  // --- Internal ---

  showBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.removeAttribute("hidden")
    }
  }

  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.setAttribute("hidden", "")
    }
  }

  closeBanner() {
    if (!this.hasBannerTarget) return
    this.bannerTarget.classList.add("consent-banner--closing")
    setTimeout(() => {
      this.hideBanner()
      this.bannerTarget.classList.remove("consent-banner--closing")
    }, 300)
  }

  loadConsent() {
    try {
      const raw = localStorage.getItem(this.constructor.STORAGE_KEY)
      if (!raw) return null
      const data = JSON.parse(raw)
      if (data.v !== this.constructor.CONSENT_VERSION) return null
      return data
    } catch {
      return null
    }
  }

  saveConsent({ analytics, marketing }) {
    this.consent = {
      necessary: true,
      analytics,
      marketing,
      ts: Date.now(),
      v: this.constructor.CONSENT_VERSION
    }
    try {
      localStorage.setItem(
        this.constructor.STORAGE_KEY,
        JSON.stringify(this.consent)
      )
    } catch {
      // localStorage unavailable (private browsing, etc.)
    }
  }

  applyConsent() {
    if (!this.consent) return

    if (this.consent.analytics) {
      this.loadGTM()
    }

    // Marketing tags are managed inside GTM container
    // (configured in GTM web UI, not here)
  }

  loadGTM() {
    // Prevent double-loading
    if (window._mmGtmLoaded) return
    window._mmGtmLoaded = true

    const gtmId = this.gtmIdValue
    const proxyUrl = this.proxyUrlValue

    if (!gtmId || !proxyUrl) return

    // Initialize dataLayer before GTM loads
    window.dataLayer = window.dataLayer || []

    // Push GA4 Measurement ID for GTM to pick up via Data Layer Variable
    if (this.hasMeasurementIdValue && this.measurementIdValue) {
      window.dataLayer.push({ mm_measurement_id: this.measurementIdValue })
    }

    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js"
    })

    // Load GTM script via proxy (async, non-blocking)
    const script = document.createElement("script")
    script.async = true
    script.src = `${proxyUrl}/gtm.js?id=${gtmId}`
    document.head.appendChild(script)

    // Show noscript iframe (for completeness, though JS is clearly running)
    const noscriptFrame = document.getElementById("gtm-noscript-frame")
    if (noscriptFrame) {
      noscriptFrame.src = `${proxyUrl}/ns.html?id=${gtmId}`
    }
  }

  isGpcEnabled() {
    return (
      navigator.globalPrivacyControl === true ||
      navigator.doNotTrack === "1" ||
      window.doNotTrack === "1"
    )
  }
}
